import type { AuthProvider } from "@refinedev/core";
import authService from "../services/authService";

/**
 * Refine auth provider that reuses the existing JWT session stored by `authService`.
 *
 * Important:
 * - This does NOT introduce a new login flow.
 * - The main app still owns login/register pages.
 * - The Admin panel just checks: "Do we have an admin JWT?"
 */
export const refineAuthProvider: AuthProvider = {
  login: async () => {
    // Login is handled by the existing /login page.
    // Returning success here keeps Refine happy if any code calls `login()`.
    return { success: true };
  },

  logout: async () => {
    authService.logout();
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const isLoggedIn = authService.isLoggedIn();
    const isAdmin = authService.isAdmin();

    if (isLoggedIn && isAdmin) {
      return {
        authenticated: true,
      };
    }

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  },

  onError: async () => {
    // If the API rejects with 401/403, we let the admin UI bounce to login.
    // Refine will call `check()` on the next render.
    return {};
  },

  getIdentity: async () => {
    const current = authService.getCurrentUser();
    if (!current?.user) return null;

    return {
      id: current.user.id,
      name: current.user.name,
      avatar: undefined,
    };
  },

  getPermissions: async () => {
    // You could expand this later (e.g. role-based resources).
    return authService.isAdmin() ? ["admin"] : [];
  },
};
